import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { HERO_IMAGE, DEALERSHIP_URL } from "@/constants/site";
import { MaskLine, useScrollTo, EASE } from "@/lib/motion";
import { resolveSiteAsset } from "@/lib/useSiteContent";
import { useTranslation } from "react-i18next";

export const Hero = ({ content = {} }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-cream pt-28 pb-16 sm:pt-32 lg:pt-40 lg:pb-24"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* Copy */}
        <div className="lg:col-span-6 xl:col-span-5">
          <h1 className="font-heading font-bold text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
            <MaskLine delay={0.05}>{content.hero_title_before || "Dostlarımız için"}</MaskLine>
            <MaskLine delay={0.18} className="text-brand">
              {content.hero_title_highlight || "sevgiyle"}
            </MaskLine>
            <MaskLine delay={0.31}>{content.hero_title_after || "üretiyoruz."}</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
            className="mt-6 max-w-md text-base sm:text-lg text-ink/70 leading-relaxed"
          >
            {content.hero_description || "Nolena Global; kaliteli üretim, güvenilir iş ortaklığı ve büyüyen ürün portföyüyle evcil dostlarımızın hayatına dokunuyor."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62, ease: EASE }}
            className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
          >
            <button
              onClick={() => scrollTo("#markalar")}
              data-testid="hero-explore-brands-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-brand/30 bg-surface px-6 py-3.5 text-sm font-semibold text-brand hover:bg-detail/50 transition-colors duration-200"
            >
              {t("hero.exploreBrands")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <a
              href={DEALERSHIP_URL}
              data-testid="hero-dealership-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-cream hover:bg-brand2 transition-colors duration-200"
            >
              {t("common.apply")}
              <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>

        {/* Image */}
        <div className="lg:col-span-6 xl:col-span-7">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: EASE }}
            className="relative rounded-[2rem] overflow-hidden border border-detail shadow-[0_30px_80px_-40px_rgba(43,33,24,0.45)]"
          >
            <div className="aspect-[4/5] sm:aspect-[16/12] lg:aspect-[4/4.4] overflow-hidden">
              <motion.img
                src={resolveSiteAsset(content.hero_image || HERO_IMAGE.url)}
                alt={content.hero_image_alt || HERO_IMAGE.alt}
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
            {/* soft spotlight */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
